import React, { useMemo, useState } from 'react';
import { confirmAction, notifyError, notifySuccess, notifyWarning } from '../utils/notify';

const GoldMaster = ({ currentUser, stores, goldRates, lastSyncedAt, onSaveGoldRate }) => {
  const isSystemAdmin = ['super admin', 'application admin', 'admin', 'system administrator'].includes((currentUser?.role || '').toLowerCase());
  const defaultStore = isSystemAdmin ? 'ALL' : (currentUser?.store || 'ALL');
  
  const [formData, setFormData] = useState({
    store: defaultStore,
    date: new Date().toISOString().split('T')[0],
    r18: '',
    r22: '',
    r24: '',
    remarks: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [historyFilterStore, setHistoryFilterStore] = useState('ALL');
  const [isAddRateModalOpen, setIsAddRateModalOpen] = useState(false);

  const filteredRates = [...goldRates]
    .reverse()
    .filter(rate => historyFilterStore === 'ALL' || rate.store === historyFilterStore);

  const getStoreDisplayName = (rate) => {
    if (rate.storeName) {
      return rate.storeName;
    }

    const matchedStore = stores.find((store) => store.code === rate.store);
    return matchedStore?.name || rate.store;
  };

  const formattedLastSynced = useMemo(() => {
    if (!lastSyncedAt) {
      return 'Not synced yet';
    }

    const parsed = new Date(lastSyncedAt);
    if (Number.isNaN(parsed.getTime())) {
      return 'Not synced yet';
    }

    return parsed.toLocaleString();
  }, [lastSyncedAt]);

  const handleSubmit = async () => {
    if (!formData.r18 || !formData.r22 || !formData.r24) {
      notifyWarning('Please fill all gold rates');
      return;
    }

    const existing = goldRates.find(r => r.date === formData.date && r.store === formData.store);
    if (existing) {
      const shouldProceed = await confirmAction({
        title: 'Rate Already Exists',
        text: 'Rates already defined for this date. The current entry will supersede the old rates. Proceed?',
        confirmButtonText: 'Yes, Update',
        cancelButtonText: 'No',
      });

      if (!shouldProceed) {
        return;
      }
    }

    try {
      setIsSaving(true);
      const response = await onSaveGoldRate({
        date: formData.date,
        time: new Date().toLocaleTimeString(),
        store: formData.store,
        r18: parseFloat(formData.r18),
        r22: parseFloat(formData.r22),
        r24: parseFloat(formData.r24),
        remarks: formData.remarks,
      });

      notifySuccess(response?.message || 'Rates Updated.');
      setFormData({
        store: defaultStore,
        date: new Date().toISOString().split('T')[0],
        r18: '',
        r22: '',
        r24: '',
        remarks: ''
      });
      setIsAddRateModalOpen(false);
    } catch (error) {
      notifyError(error.message || 'Failed to update rates');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section>
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between gap-3 mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold">Gold Rate Management</h2>
          <button
            onClick={() => setIsAddRateModalOpen(true)}
            className="inline-flex items-center gap-2 bg-jewel-red text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition text-sm md:text-base"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>ADD PRICE</span>
          </button>
        </div>
        <div className="mt-6 md:mt-8 mb-3 md:mb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h3 className="font-bold text-gray-500 uppercase text-xs md:text-sm">Rate History</h3>
            <p className="text-xs text-gray-400 mt-1">Last synced: {formattedLastSynced}</p>
          </div>
          <div className="w-full md:w-64">
            <label className="block text-xs md:text-sm font-bold mb-2">Filter by Store</label>
            <select 
              value={historyFilterStore}
              onChange={(e) => setHistoryFilterStore(e.target.value)}
              className="w-full p-2 border rounded text-sm md:text-base"
            >
              <option value="ALL">All Stores</option>
              {stores.map(store => (
                <option key={store.code} value={store.code}>{store.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="space-y-2">
          {filteredRates.map((rate, index) => (
            <div key={index} className="text-xs md:text-sm border-l-4 border-red-500 bg-white p-2 md:p-3 shadow-sm">
              <div className="flex flex-col md:flex-row md:justify-between gap-1 md:gap-2">
                <span className="font-semibold">
                  <b>{rate.date}</b> ({rate.time}) - Store: {getStoreDisplayName(rate)}
                </span>
                <span className="text-jewel-red font-medium">
                  24K: ₹{rate.r24} | 22K: ₹{rate.r22} | 18K: ₹{rate.r18}
                </span>
              </div>
            </div>
          ))}
          {filteredRates.length === 0 && (
            <div className="text-center py-6 text-gray-400">
              <p className="text-sm">No rate history found for the selected store</p>
            </div>
          )}
        </div>
      </div>

      {isAddRateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg border border-gray-200">
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg md:text-xl font-bold">Add Gold Price</h3>
              <button
                onClick={() => setIsAddRateModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 font-bold text-sm"
              >
                CLOSE
              </button>
            </div>
            <div className="p-4 md:p-6 bg-red-50 rounded-b-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isSystemAdmin && (
                  <div>
                    <label className="block text-xs md:text-sm font-bold mb-1">Target Store</label>
                    <select
                      value={formData.store}
                      onChange={(e) => setFormData({ ...formData, store: e.target.value })}
                      className="w-full p-2 border rounded text-sm md:text-base"
                    >
                      <option value="ALL">Apply to All Stores</option>
                      {stores.map(store => (
                        <option key={store.code} value={store.code}>{store.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1">Effective Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full p-2 border rounded text-sm md:text-base"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold mb-2">Gold Rates (per gram)</label>
                <div className="grid grid-cols-3 gap-2 md:gap-4">
                  <div>
                    <label className="block text-xs font-bold mb-1 text-center">18K (₹)</label>
                    <input
                      type="number"
                      value={formData.r18}
                      onChange={(e) => setFormData({ ...formData, r18: e.target.value })}
                      className="w-full p-2 border rounded text-center text-sm"
                      placeholder="5200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1 text-center">22K (₹)</label>
                    <input
                      type="number"
                      value={formData.r22}
                      onChange={(e) => setFormData({ ...formData, r22: e.target.value })}
                      className="w-full p-2 border rounded text-center text-sm"
                      placeholder="6500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1 text-center">24K (₹)</label>
                    <input
                      type="number"
                      value={formData.r24}
                      onChange={(e) => setFormData({ ...formData, r24: e.target.value })}
                      className="w-full p-2 border rounded text-center text-sm"
                      placeholder="7100"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold mb-1">Remarks</label>
                <input
                  type="text"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  className="w-full p-2 border rounded text-sm md:text-base"
                  placeholder="Optional remarks"
                />
              </div>

              <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-2">
                <button
                  onClick={() => setIsAddRateModalOpen(false)}
                  disabled={isSaving}
                  className="w-full md:w-auto bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-bold hover:bg-gray-300 transition text-sm md:text-base disabled:opacity-60"
                >
                  CANCEL
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-jewel-red text-white px-8 md:px-10 py-2 rounded-lg font-bold hover:bg-red-700 transition text-sm md:text-base disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    'SAVING...'
                  ) : (
                    <>
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>UPDATE RATES</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GoldMaster;
